/**
 * Returns the URL only when it is a real web address that is safe to place in
 * an anchor `href`. Any non-`http(s)` scheme — most importantly `javascript:`,
 * `data:` and `vbscript:` — yields `null` so the caller renders plain text
 * instead of navigable, script-executing chrome.
 *
 * Source URLs on regulatory publications are recorded by the inspector agent
 * from pages it reads, so they are attacker-influencable and must never reach
 * an href unchecked (Strix scan 5, stored-XSS class). `FindingSource` already
 * applies this rule inline; this helper is the single shared gate for every
 * other publication/source link.
 */
export function safeExternalHref(url: string | null | undefined): string | null {
  if (typeof url !== "string") return null;
  const trimmed = url.trim();
  if (trimmed === "") return null;
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return null;
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return null;
  return parsed.href;
}

/** The registrable-ish host shown next to a link, `www.` stripped, or null. */
export function externalHrefHost(url: string | null | undefined): string | null {
  const href = safeExternalHref(url);
  if (href === null) return null;
  try {
    return new URL(href).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}
